// Per-currency pay-in configuration — CLIENT MIRROR of server
// lib/payinConfig.js (founder-approved table). If either side
// changes, change both — the server is authoritative; this copy only drives
// UI (chips, bounds shown in copy, popup trigger, price grids).

export interface PayinCurrencyConfig {
  minPrice: number;
  // DEFAULT ceilings (fraud door, founder ruling: $200 request /
  // $200 boost / $400 counter usd-class, scaled per currency). Every talent
  // runs on these until an admin lifts them.
  maxPrice: number;
  defaultPrice: number;
  chips: number[];
  stepMinor: number;
  threeDSMinor: number;
  maxBoost: number;
  maxCounter: number;
  // LIFTED ceilings — the pre-fraud-door numbers, applied only to a talent an
  // admin has vouched for (User.capsLifted). Never unlimited.
  liftedMaxPrice: number;
  liftedMaxBoost: number;
  liftedMaxCounter: number;
  flatFee: number;
  setlistFloor: number;
  // ⭐ FAN BOOST nudge threshold (MAJOR units, founder ladder): the
  // "Boost it · ★300" chip shows only on pending requests strictly below
  // this. NEW CURRENCIES MUST FILL THIS COLUMN — server table is the
  // authority (lib/payinConfig.js, integrity-tested); keep in lockstep.
  boostNudgeBelow: number;
  // Minimum cashout ($5-class since 2026-09-21, was $20-class: each row's
  // first price chip) — mirrors the server table; /orders/earnings sends the
  // live value, this is the fallback (and the zero-balance Instant card).
  /** The lowest request price a talent may set (founder 2026-10-30) — mirror of the server. */
  minRequest: number;
  minCashout: number;
}

export const PAYIN_CONFIG: Record<string, PayinCurrencyConfig> = {
  usd: { minPrice: 2, maxPrice: 200, defaultPrice: 5, chips: [5, 20, 50], stepMinor: 50, threeDSMinor: 3000, maxBoost: 200, maxCounter: 400, liftedMaxPrice: 1000, liftedMaxBoost: 2000, liftedMaxCounter: 10000, flatFee: 0.3, boostNudgeBelow: 20, minRequest: 5, minCashout: 15, setlistFloor: 4.99 },
  eur: { minPrice: 2, maxPrice: 200, defaultPrice: 5, chips: [5, 20, 50], stepMinor: 50, threeDSMinor: 3000, maxBoost: 200, maxCounter: 400, liftedMaxPrice: 1000, liftedMaxBoost: 2000, liftedMaxCounter: 10000, flatFee: 0.3, boostNudgeBelow: 20, minRequest: 5, minCashout: 15, setlistFloor: 4.99 },
  gbp: { minPrice: 2, maxPrice: 150, defaultPrice: 5, chips: [5, 20, 50], stepMinor: 50, threeDSMinor: 2500, maxBoost: 150, maxCounter: 300, liftedMaxPrice: 800, liftedMaxBoost: 1500, liftedMaxCounter: 8000, flatFee: 0.25, boostNudgeBelow: 20, minRequest: 5, minCashout: 15, setlistFloor: 4.99 },
  cad: { minPrice: 3, maxPrice: 250, defaultPrice: 7, chips: [5, 20, 50], stepMinor: 50, threeDSMinor: 4000, maxBoost: 250, maxCounter: 500, liftedMaxPrice: 1400, liftedMaxBoost: 3000, liftedMaxCounter: 15000, flatFee: 0.4, boostNudgeBelow: 25, minRequest: 5, minCashout: 20, setlistFloor: 6.99 },
  chf: { minPrice: 2, maxPrice: 200, defaultPrice: 5, chips: [5, 20, 50], stepMinor: 50, threeDSMinor: 3000, maxBoost: 200, maxCounter: 400, liftedMaxPrice: 1000, liftedMaxBoost: 2000, liftedMaxCounter: 10000, flatFee: 0.3, boostNudgeBelow: 20, minRequest: 5, minCashout: 15, setlistFloor: 4.99 },
  sek: { minPrice: 20, maxPrice: 2000, defaultPrice: 50, chips: [50, 200, 500], stepMinor: 100, threeDSMinor: 30000, maxBoost: 2000, maxCounter: 4000, liftedMaxPrice: 10000, liftedMaxBoost: 20000, liftedMaxCounter: 100000, flatFee: 3.5, boostNudgeBelow: 200, minRequest: 50, minCashout: 150, setlistFloor: 49 },
  dkk: { minPrice: 15, maxPrice: 1500, defaultPrice: 50, chips: [50, 200, 500], stepMinor: 100, threeDSMinor: 22500, maxBoost: 1500, maxCounter: 3000, liftedMaxPrice: 7000, liftedMaxBoost: 15000, liftedMaxCounter: 75000, flatFee: 2.5, boostNudgeBelow: 150, minRequest: 50, minCashout: 100, setlistFloor: 39 },
  nok: { minPrice: 20, maxPrice: 2000, defaultPrice: 50, chips: [50, 200, 500], stepMinor: 100, threeDSMinor: 30000, maxBoost: 2000, maxCounter: 4000, liftedMaxPrice: 10000, liftedMaxBoost: 20000, liftedMaxCounter: 100000, flatFee: 3.5, boostNudgeBelow: 200, minRequest: 50, minCashout: 150, setlistFloor: 49 },
  pln: { minPrice: 8, maxPrice: 800, defaultPrice: 20, chips: [20, 100, 200], stepMinor: 100, threeDSMinor: 12500, maxBoost: 800, maxCounter: 1600, liftedMaxPrice: 4000, liftedMaxBoost: 8000, liftedMaxCounter: 40000, flatFee: 1.5, boostNudgeBelow: 80, minRequest: 15, minCashout: 40, setlistFloor: 20 },
  czk: { minPrice: 50, maxPrice: 5000, defaultPrice: 120, chips: [100, 500, 1000], stepMinor: 100, threeDSMinor: 75000, maxBoost: 5000, maxCounter: 10000, liftedMaxPrice: 24000, liftedMaxBoost: 50000, liftedMaxCounter: 250000, flatFee: 8, boostNudgeBelow: 500, minRequest: 80, minCashout: 250, setlistFloor: 120 },
  huf: { minPrice: 750, maxPrice: 80000, defaultPrice: 2000, chips: [2000, 8000, 20000], stepMinor: 5000, threeDSMinor: 1200000, maxBoost: 80000, maxCounter: 160000, liftedMaxPrice: 400000, liftedMaxBoost: 800000, liftedMaxCounter: 900000, flatFee: 120, boostNudgeBelow: 8000, minRequest: 1500, minCashout: 4000, setlistFloor: 1950 },
  ron: { minPrice: 10, maxPrice: 1000, defaultPrice: 25, chips: [25, 100, 250], stepMinor: 100, threeDSMinor: 15000, maxBoost: 1000, maxCounter: 2000, liftedMaxPrice: 5000, liftedMaxBoost: 10000, liftedMaxCounter: 50000, flatFee: 1.5, boostNudgeBelow: 100, minRequest: 15, minCashout: 50, setlistFloor: 25 },
};

// The Profile dropdown / display order for the live pay-in currencies.
export const PAYIN_CURRENCIES = [
  "usd", "eur", "gbp", "cad", "chf", "sek", "dkk", "nok", "pln", "czk", "huf", "ron",
] as const;

export type PayinCurrency = (typeof PAYIN_CURRENCIES)[number];

// Flag shown next to each currency in the Profile dropdown (founder-approved
// mockup v2). EUR gets the EU flag.
export const PAYIN_CURRENCY_FLAGS: Record<string, string> = {
  usd: "🇺🇸", eur: "🇪🇺", gbp: "🇬🇧", cad: "🇨🇦", chf: "🇨🇭", sek: "🇸🇪",
  dkk: "🇩🇰", nok: "🇳🇴", pln: "🇵🇱", czk: "🇨🇿", huf: "🇭🇺", ron: "🇷🇴",
};

// Unknown/missing → usd behavior (exactly the pre-Phase-C shape). Mirrors the
// server's configFor.
export function configFor(currency?: string | null): PayinCurrencyConfig {
  return PAYIN_CONFIG[String(currency || "usd").toLowerCase()] || PAYIN_CONFIG.usd;
}

// ─── Cap tiers (fraud door) — EXACT mirror of server capsFor ───
// `lifted` accepts the talent's capsLifted flag (boolean) or an object that
// carries it (a profile / DJ payload). Anything else → DEFAULT tier, the
// safe direction. The server re-validates authoritatively with the same rule.
export type CapsLiftedInput = boolean | { capsLifted?: boolean | null } | null | undefined;

export function capsLiftedFrom(lifted: CapsLiftedInput): boolean {
  if (lifted === true) return true;
  return !!(lifted && typeof lifted === "object" && lifted.capsLifted === true);
}

export function capsFor(currency?: string | null, lifted?: CapsLiftedInput) {
  const cfg = configFor(currency);
  if (capsLiftedFrom(lifted)) {
    return { maxPrice: cfg.liftedMaxPrice, maxBoost: cfg.liftedMaxBoost, maxCounter: cfg.liftedMaxCounter, lifted: true };
  }
  return { maxPrice: cfg.maxPrice, maxBoost: cfg.maxBoost, maxCounter: cfg.maxCounter, lifted: false };
}

export const minPriceFor = (c?: string | null) => configFor(c).minPrice;

// The fan bid box's − / + step: one whole currency step ($1 · 10 kr · 500 Ft).
// It used to read minPrice; when the floor rose to the $2-class (2026-10-22)
// the step stayed where fans know it, so it lives in its own small table.
// Client-only UI steering — the server never sees it.
const BID_STEP: Record<string, number> = {
  usd: 1, eur: 1, gbp: 1, cad: 1, chf: 1, sek: 10, dkk: 10, nok: 10, pln: 5, czk: 25, huf: 500, ron: 5,
};
export const bidStepFor = (c?: string | null) => BID_STEP[String(c || "usd").toLowerCase()] ?? BID_STEP.usd;
export const maxPriceFor = (c?: string | null, lifted?: CapsLiftedInput) => capsFor(c, lifted).maxPrice;
export const defaultPriceFor = (c?: string | null) => configFor(c).defaultPrice;
export const chipsFor = (c?: string | null) => configFor(c).chips.slice();
export const maxBoostFor = (c?: string | null, lifted?: CapsLiftedInput) => capsFor(c, lifted).maxBoost;
export const maxCounterFor = (c?: string | null, lifted?: CapsLiftedInput) => capsFor(c, lifted).maxCounter;
export const flatFeeFor = (c?: string | null) => configFor(c).flatFee;
export const boostNudgeBelowFor = (c?: string | null) => configFor(c).boostNudgeBelow;
export const minCashoutFor = (c?: string | null) => configFor(c).minCashout;

// ─── Talent minimums — mirror of server lib/payinConfig.js (founder 2026-10-30) ───
// Each row's minRequest ($5 class; ~$3.50 in PL/CZ/HU/RO) is the lowest price
// a talent sets for a request or counter offer; minCashout ($15 class) the
// withdrawal line. Tips, boosts and playlists keep minPrice ($2 class).
// A talent's own number — the MassPay-country line or a superadmin exception
// — comes from the server on their profile (minRequestPrice); withdrawal
// lines come per talent on /orders/earnings. These are the fallbacks.
export type TierTalent = { currency?: string | null; minRequestPrice?: number | null } | null | undefined;
export const minRequestFor = (c?: string | null) => configFor(c).minRequest;
/** The lowest price this talent may set for a request or a counter offer. */
export const minRequestPriceFor = (c: string | null | undefined, talent: TierTalent) => {
  const own = talent && typeof talent === "object" ? Number(talent.minRequestPrice) : NaN;
  return Number.isFinite(own) && own > 0 ? own : minRequestFor(c);
};

// DJ-side "protect your cashout" nudge threshold (RequestsPage) — the $100
// class in round local numbers. UI-only.
const CASHOUT_PROTECT: Record<string, number> = {
  usd: 100, eur: 100, gbp: 100, cad: 100, chf: 100,
  sek: 1000, dkk: 1000, nok: 1000, pln: 400, czk: 2500, huf: 40000, ron: 500,
};
export const cashoutProtectThresholdFor = (c?: string | null) =>
  CASHOUT_PROTECT[String(c || "usd").toLowerCase()] ?? 100;

// The one price-grid rounder, per currency — BYTE-MIRROR of the server's
// roundUpToStep (usd/eur step 50 ≡ the historic half-dollar grid; HUF 50 Ft
// so prices/totals are always whole forints).
export function roundUpToStep(amountMajor: number, currency?: string | null): number {
  if (typeof amountMajor !== "number" || !isFinite(amountMajor)) return 0;
  const minor = Math.max(0, Math.round(amountMajor * 100 + 1e-6));
  const step = configFor(currency).stepMinor;
  return (Math.ceil(minor / step) * step) / 100;
}

// The popup-trigger predicate — EXACT mirror of server lib/payinConfig.js
// needsPriceSetup (contract pinned by server tests/price-pending-setup.test.js):
// show the first-sign-in price popup iff the account still carries its
// registration placeholder AND payouts are verified. Absent fields (every
// pre-Phase-C DJ) → false, always.
export function needsPriceSetup(user?: { pricePendingSetup?: boolean; isStripeOnboarded?: boolean; canTakeMoney?: boolean } | null): boolean {
  // Free tier: no price to set until the talent can take money.
  if (user?.canTakeMoney === false) return false;
  return !!(user && user.pricePendingSetup && user.isStripeOnboarded);
}

// Client-side price validation mirroring the server's validatePrice — used
// for instant popup/profile feedback; the server re-validates authoritatively.
export function validatePrice(rawPrice: unknown, currency?: string | null, lifted?: CapsLiftedInput, talent?: TierTalent): { ok: boolean; price?: number } {
  const n = Number(rawPrice);
  if (!isFinite(n)) return { ok: false };
  const minPrice = minRequestPriceFor(currency, talent);
  const { maxPrice } = capsFor(currency, lifted);
  if (n === 0) return { ok: true, price: 0 };
  if (n < minPrice || n > maxPrice) return { ok: false };
  const snapped = roundUpToStep(n, currency);
  if (snapped > maxPrice) return { ok: false };
  return { ok: true, price: snapped };
}

// ─── Setlist sale pricing — EXACT mirror of server lib/payinConfig.js ───────
//
// setlistFloor — the "$4.99-class" minimum per currency: a clean LOCAL
// number (never an FX conversion). HUF stays on the whole-50-Ft grid (1950).
//
// setlistChipsFor(requestPrice, currency) — the three suggested chips,
// anchored to the DJ's OWN request price: ~2x / ~3x / ~4x (founder:
// the bottom chip is DOUBLE the request price; "setlists are
// very valuable, encourage DJs to charge more"), snapped per currency
// class and clamped to the floor:
//   • usd-class (step-50 minor grids): X.99 endings — floor(x) − 0.01.
//       $5 request  → 9.99 / 14.99 / 19.99
//       $20 request → 39.99 / 59.99 / 79.99
//   • whole-unit grids (sek/dkk/nok/pln/czk/ron) and HUF: whole local
//     amounts on the currency's own stepMinor grid — no fake .99.
// Chips are forced ascending-distinct by re-snapping upward on collision.

export const setlistFloorFor = (c?: string | null) => configFor(c).setlistFloor;

function snapSetlist(amountMajor: number, currency?: string | null): number {
  const cfg = configFor(currency);
  if (cfg.stepMinor <= 50) {
    // usd-class: X.99 psychology
    return Math.max(cfg.setlistFloor, Math.floor(amountMajor) - 0.01);
  }
  // whole-unit grids: round to the currency's own step, never below floor
  const stepMajor = cfg.stepMinor / 100;
  const snapped = Math.round(amountMajor / stepMajor) * stepMajor;
  return Math.max(cfg.setlistFloor, snapped);
}

export function setlistChipsFor(requestPrice: unknown, currency?: string | null): number[] {
  const cfg = configFor(currency);
  const base = Number(requestPrice) > 0 ? Number(requestPrice) : cfg.defaultPrice;
  const chips: number[] = [];
  for (const mult of [2, 3, 4]) {
    let chip = snapSetlist(base * mult, currency);
    // ascending + distinct: on collision, push up one grid step (or +1.00
    // for the X.99 class, preserving the .99 ending)
    const bump = cfg.stepMinor <= 50 ? 1 : cfg.stepMinor / 100;
    while (chips.length && chip <= chips[chips.length - 1]) chip = +(chip + bump).toFixed(2);
    chips.push(+chip.toFixed(2));
  }
  return chips;
}

// Validation for the DJ-chosen setlist price (chip tap or Custom pad).
//
// FLOOR REMOVED (founder: "can be any price even free"). The old
// gate was `setlistFloor` — an earlier PRODUCT rule ("setlists are very
// valuable, encourage DJs to charge more"), never a technical limit. It now
// survives only as the clamp on the three SUGGESTED chips above; the DJ's own
// number is bounded by the same per-currency minPrice their song requests
// already use, which clears Stripe's per-currency minimum with room to spare.
//
// Free (0) is a first-class price and skips Stripe entirely (there is no such
// thing as a zero-amount PaymentIntent).
//
// null / undefined / "" are NOT free: they mean "never set a price", and must
// stay invalid so `setlistSaleEnabled` can't be switched on behind an unset
// price and quietly advertise a free setlist the DJ never chose.
//
// EXACT mirror of the server's lib/payinConfig.js — these two move together.
export function validateSetlistPrice(rawPrice: unknown, currency?: string | null, lifted?: CapsLiftedInput): { ok: boolean; price?: number } {
  if (rawPrice === null || rawPrice === undefined || rawPrice === "") return { ok: false };
  const n = Number(rawPrice);
  if (!isFinite(n) || n < 0) return { ok: false };
  if (n === 0) return { ok: true, price: 0 };
  const cfg = configFor(currency);
  const { maxPrice } = capsFor(currency, lifted);
  if (n < cfg.minPrice || n > maxPrice) return { ok: false };
  // Custom amounts keep the DJ's exact number if it's grid-legal for the
  // currency; usd-class accepts any cent amount >= minPrice, whole-unit grids
  // snap up to their step.
  if (cfg.stepMinor <= 50) return { ok: true, price: +n.toFixed(2) };
  const snapped = roundUpToStep(n, currency);
  if (snapped > maxPrice) return { ok: false };
  return { ok: true, price: snapped };
}
